import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { chartDateRangeOptions } from '@/modules/common/constants/chart-date-range-oprions.ts';
import { DATE_RANGE_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { mapDateRangeToObj } from '@/modules/issuer-dashboard/helpers/mapDateRangeToObj.ts';
import {
  type NormaliseNavData,
  normaliseNavDataAsync,
} from '@/modules/issuer-dashboard/helpers/normalizeNavData.ts';
import { useNavHistoryListQuery } from '@/modules/issuer-dashboard/queries/nav.tsx';
import { BalanceChartCanvas, ChartEmptyState, InfoCard, SelectButtons, Spinner } from 'ui';

const chartColors = [
  '#0369A1',
  '#0278B5',
  '#0389C7',
  '#01A0D8',
  '#25B0E5',
  '#4BC1F1',
  '#77D1F9',
  '#77D1F9',
  '#77D1F9',
  '#A6E1FD',
];

interface NavProps {
  showFullName?: boolean;
  withFuture?: boolean;
}

const Nav = ({ showFullName = true, withFuture = true }: NavProps) => {
  const [params, setParams] = useSearchParams();
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const date_range = params.get(DATE_RANGE_SEARCH_PARAM_KEY + '1') || 'all';
  const getRegisterTypeStrings = useRegisterTypeStrings();

  const { data, isLoading, isError } = useNavHistoryListQuery(
    mapDateRangeToObj(date_range || '', withFuture),
    false,
    registerId,
  );
  const [chartSeries, setChartSeries] = useState<NormaliseNavData[]>([]);
  const [isNormalizing, setIsNormalizing] = useState(false);

  const handleChangeDateRange = useCallback(
    (date_range: string) => {
      params.set(DATE_RANGE_SEARCH_PARAM_KEY + '1', date_range);
      setParams(params);
    },
    [params, setParams],
  );

  useEffect(() => {
    let cancelled = false;

    setIsNormalizing(true);

    void normaliseNavDataAsync(data)
      .then(result => {
        if (!cancelled) {
          setChartSeries(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setChartSeries([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsNormalizing(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [data]);

  const isPresentChartRecords = useMemo(
    () => chartSeries.some(({ data }) => data.length),
    [chartSeries],
  );
  //
  return (
    <InfoCard
      title={`${getRegisterTypeStrings('nav')}${showFullName ? ' History' : ''}`}
      headerAction={
        <SelectButtons
          options={chartDateRangeOptions}
          selected={chartDateRangeOptions.find(({ value }) => value === date_range)}
          onChange={handleChangeDateRange}
          className="sm:w-[375px] w-[300px]"
          disabled={isLoading}
        />
      }
    >
      {!isLoading && !isNormalizing && isPresentChartRecords && (
        <BalanceChartCanvas
          data={chartSeries}
          height="300px"
          type="line"
          fill={false}
          withDateTime={true}
          colors={chartColors}
          countsLabel={6}
          markerStrokeColor="#FFFFFF"
        />
      )}
      {(isLoading || isNormalizing) && !chartSeries.length && (
        <div className="flex flex-col w-full h-[200px] items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}
      {!isLoading && !isNormalizing && !chartSeries?.length && (
        <div className="py-4 flex justify-center items-center font-medium text-lg">
          <ChartEmptyState isError={isError} isLoading={isLoading} />
        </div>
      )}
      {!isLoading && !isNormalizing && !isPresentChartRecords && (
        <div className="w-full h-[200px] flex items-center justify-center text-2xl py-6">
          No data yet
        </div>
      )}
    </InfoCard>
  );
};

export default Nav;
